import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { AddEventModal } from '../../modal/AddEventModal';
import { api } from '../../services/api';
import { Pagination } from '../../components/admin/Pagination';
import { EventList } from '../../components/admin/EventList';
import { EventFilters } from '../../components/admin/EventFilters';

export const Dashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);

  // Fetch events
  const fetchEvents = async (page = 1, filterParams = {}) => {
    setIsLoading(true);
    try {
      const response = await api.getEvents({
        page,
        limit: pagination.limit,
        ...filterParams,
      });

      setEvents(response.events);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching events:', error);
      alert('Failed to fetch events');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchEvents();
  }, []);

  // Handle create/update event
  const handleSubmit = async (formData) => {
    console.log('Form Data Received:', formData);
    
    setIsLoading(true);
    try {
      // Extract thumbnail file from formData
      const thumbnailFile = formData.thumbnail;
      
      console.log('Thumbnail File:', thumbnailFile);
      console.log('Editing Event:', editingEvent);

      // Check if thumbnail is required (new event without thumbnail)
      if (!thumbnailFile && !editingEvent?.thumbnail) {
        alert('Please upload a thumbnail image');
        setIsLoading(false);
        return;
      }

      let thumbnailUrl = editingEvent?.thumbnail || ''; // Use existing thumbnail for edits

      // Upload new thumbnail if a file was selected
      if (thumbnailFile instanceof File) {
        console.log('Uploading thumbnail file...');
        
        try {
          const imageResponse = await api.uploadThumbnail(thumbnailFile);
          console.log('Thumbnail upload response:', imageResponse);
          
          if (!imageResponse.success) {
            throw new Error(imageResponse.message || 'Failed to upload thumbnail');
          }
          
          // Get the thumbnail URL from response
          thumbnailUrl = imageResponse.thumnbail;
          console.log('Thumbnail uploaded successfully:', thumbnailUrl);
        } catch (uploadError) {
          console.error('Thumbnail upload error:', uploadError);
          throw new Error(`Thumbnail upload failed: ${uploadError.message}`);
        }
      } else {
        console.log('No new thumbnail file, using existing or preview URL');
        // If thumbnailPreview exists but no file (editing case), keep existing thumbnail
        if (formData.thumbnailPreview && !thumbnailFile) {
          thumbnailUrl = editingEvent?.thumbnail || formData.thumbnailPreview;
        }
      }

      // Validate thumbnail URL exists
      if (!thumbnailUrl) {
        throw new Error('Thumbnail URL is missing');
      }

      // Helper function to process pricing
      const processPricing = (priceData) => {
        if (!priceData || typeof priceData !== 'object') {
          return { price: 0, discountedPrice: 0 };
        }
        
        return {
          price: priceData.price === "" || priceData.price === null || priceData.price === undefined 
            ? 0 
            : parseFloat(priceData.price) || 0,
          discountedPrice: priceData.discountedPrice === "" || priceData.discountedPrice === null || priceData.discountedPrice === undefined
            ? 0 
            : parseFloat(priceData.discountedPrice) || 0
        };
      };

      // Helper function to process age restrictions
      const processAge = (ageData) => {
        if (!ageData || typeof ageData !== 'object') {
          return { minAge: null, maxAge: null };
        }
        
        return {
          minAge: ageData.minAge === "" || ageData.minAge === null || ageData.minAge === undefined
            ? null
            : String(ageData.minAge),
          maxAge: ageData.maxAge === "" || ageData.maxAge === null || ageData.maxAge === undefined
            ? null
            : String(ageData.maxAge)
        };
      };

      // Prepare event data with NEW BACKEND MODEL
      const eventData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription,
        date: formData.date,
        time: formData.time,
        thumbnail: thumbnailUrl,
        pincode: formData.pincode,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        venue: formData.venue,
        location: formData.location,
        whatToExpect: formData.whatToExpect || '',
        tags: formData.tags || [],
        audience: formData.audience || 'all',
        // NEW: Age restrictions
        age: processAge(formData.age),
        capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
        // Nested Male and Female pricing
        malePrice: processPricing(formData.malePrice),
        femalePrice: processPricing(formData.femalePrice),
        currency: formData.currency || 'INR',
        status: formData.status || 'draft',
        communityLinks: formData.communityLinks || null,
        coordinates: formData.coordinates || {
          type: "Point",
          coordinates: [0, 0]
        },
        showDummyUsers: formData.showDummyUsers || false,
        appliedGst: formData.appliedGst || false,
      };

      console.log('Event Data to submit:', eventData);
      console.log('Male Price:', eventData.malePrice);
      console.log('Female Price:', eventData.femalePrice);
      console.log('Age Restrictions:', eventData.age);
      console.log('Applied GST:', eventData.appliedGst);

      // Validation: Check if discounted price is greater than regular price
      if (eventData.malePrice.discountedPrice > 0 && eventData.malePrice.discountedPrice > eventData.malePrice.price) {
        throw new Error('Male discounted price cannot be greater than regular price');
      }

      if (eventData.femalePrice.discountedPrice > 0 && eventData.femalePrice.discountedPrice > eventData.femalePrice.price) {
        throw new Error('Female discounted price cannot be greater than regular price');
      }

      // Validation: Check age restrictions
      if (eventData.age.minAge && eventData.age.maxAge) {
        const minAge = parseInt(eventData.age.minAge);
        const maxAge = parseInt(eventData.age.maxAge);
        
        if (minAge > maxAge) {
          throw new Error('Minimum age cannot be greater than maximum age');
        }
        
        if (minAge < 0 || maxAge < 0) {
          throw new Error('Age cannot be negative');
        }
      }

      // Create or update
      if (editingEvent) {
        const response = await api.updateEvent(editingEvent._id, eventData);
        console.log('Update response:', response);
        alert('Event updated successfully!');
      } else {
        const response = await api.createEvent(eventData);
        console.log('Create response:', response);
        alert('Event created successfully!');
      }

      // Close modal and refresh
      setIsModalOpen(false);
      setEditingEvent(null);
      await fetchEvents(pagination.page, filters);
      
    } catch (error) {
      console.error('Error saving event:', error);
      alert(error.message || 'Failed to save event. Please check console for details.');
      // Don't close modal on error so user can fix issues
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (event) => {
    console.log('Editing event:', event);
    
    // Transform event data for editing
    const editData = {
      ...event,
      // Ensure nested pricing structure
      malePrice: event.malePrice && typeof event.malePrice === 'object' 
        ? event.malePrice 
        : { price: event.malePrice || 0, discountedPrice: 0 },
      femalePrice: event.femalePrice && typeof event.femalePrice === 'object'
        ? event.femalePrice
        : { price: event.femalePrice || 0, discountedPrice: 0 },
      // Ensure nested age structure
      age: event.age && typeof event.age === 'object'
        ? event.age
        : { minAge: null, maxAge: null }
    };
    
    console.log('Transformed edit data:', editData);
    console.log('Edit data age:', editData.age);
    setEditingEvent(editData);
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await api.deleteEvent(eventId);
      alert('Event deleted successfully!');
      fetchEvents(pagination.page, filters);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    console.log('Applying filters:', newFilters);
    setFilters(newFilters);
    fetchEvents(1, newFilters);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    console.log('Resetting filters');
    setFilters({});
    fetchEvents(1, {});
  };

  // Handle page change
  const handlePageChange = (page) => {
    console.log('Changing to page:', page);
    fetchEvents(page, filters);
  };

  // Handle modal close
  const handleModalClose = () => {
    console.log('Closing modal');
    setIsModalOpen(false);
    setEditingEvent(null);
  };

  return (
    <div className='px-2 pt-2'>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B3C5C]">Events</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your events ({pagination.total} total)
          </p>
        </div>
        <button
          onClick={() => {
            console.log('Opening create event modal');
            setEditingEvent(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#F4A696] to-[#E89580] md:px-6 md:py-3 py-1 px-2 text-white font-medium hover:shadow-lg transition-all"
        >
          <Plus className="h-5 w-5" />
          Create Event
        </button>
      </div>

      {/* Filters */}
      <EventFilters
        onFilterChange={handleFilterChange}
        onReset={handleFilterReset}
      />

      {/* Event List */}
      <EventList
        events={events}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={isLoading}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />

      {/* Modal */}
      <AddEventModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        initialData={editingEvent}
        isLoading={isLoading}
      />
    </div>
  );
};
